import { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { ShortAbsence, VacationBalances } from '~/data/absences';
import { useEmployeeAbsenceData } from '~/data/absences/useEmployeeAbsenceData';
import { useApplicationConfig } from '~/data/applicationConfig';
import { useCompanyData } from '~/data/company/useCompanyData';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { useSWR } from '~/hooks/useSWR';
import { useVisible } from '~/hooks/useVisible';

import { getVacHistoryTableData, vacationDaysHistoryData } from './data';

export type UseFormsOutput = {
  defaultData?: VacationBalances;
  shortTermAbsences: ShortAbsence[];
  vacHistoryTableData: vacationDaysHistoryData[];
  bnEnableVacBank: boolean;
  bnEnableVacBalance: boolean;
  vacBankType1: boolean;
  vacBankType2: boolean;
  vacBankType3: boolean;
  openDiaolog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  isAbsHistoryDialogOpen: boolean;
  closeAbsHistoryDialog: () => void;
  openAbsHistoryDialog: () => void;
  extraVacEnum: AbsEnum[];
  absShortReasonEnum: AbsEnum[];
  vacBankEnum: AbsEnum[];
  editMode: boolean;
  setEditMode: Dispatch<SetStateAction<boolean>>;
};

export type AbsEnum = {
  code: string;
  extCode?: string;
  name: string;
  description?: string;
};

export const useVacationBalanceData = (): UseFormsOutput => {
  // Load configs
  const { applicationConfig } = useApplicationConfig();
  const { companyData } = useCompanyData();
  const {
    bnEnableVacBank,
    bnEnableVacBalance,
    vacBankType1,
    vacBankType2,
    vacBankType3,
  } = useMemo(() => {
    const absenceConfig = applicationConfig?.fComConfig?.absConfig;
    const leaveTypes = companyData?.config?.fComLeaveTypes;

    return {
      bnEnableVacBank: !!absenceConfig?.fComEnableVacationBank,
      bnEnableVacBalance: !!absenceConfig?.fComEnableYearlyVacationBalance,
      vacBankType1: leaveTypes?.fComVacationBankLeaveTypes1?.length === 0,
      vacBankType2: leaveTypes?.fComVacationBankLeaveTypes2?.length === 0,
      vacBankType3: leaveTypes?.fComVacationBankLeaveTypes3?.length === 0,
    };
  }, [applicationConfig, companyData]);

  const { getEnums } = useAllEnums();
  const { vacBankEnum, extraVacEnum, absShortReasonEnum } = useMemo(() => {
    return {
      vacBankEnum: getEnums(EnumListKey.VACATION_BANK_LABEL),
      extraVacEnum: getEnums(EnumListKey.VACATION_BALANCE_LABEL),
      absShortReasonEnum: getEnums(EnumListKey.ABSENCE_SHORT_REASON),
    };
  }, [getEnums]);

  // Load absence data
  const { selectedEmployeeId } = useSelectedEmployeeId();
  const empId = String(selectedEmployeeId);

  const { absencesData } = useEmployeeAbsenceData({
    employeeId: selectedEmployeeId,
  });

  const { defaultData, shortTermAbsences } = useMemo(() => {
    const defaultData = absencesData?.vacationBalance;
    const shortTermAbsences = absencesData?.absenceShorts ?? [];

    return {
      defaultData,
      shortTermAbsences,
    };
  }, [absencesData]);

  const { data: vacHistoryResponse } = useSWR(
    ['getVacHistoryTableData', empId],
    () => getVacHistoryTableData(empId),
  );
  const vacHistoryTableData = vacHistoryResponse as vacationDaysHistoryData[];

  // States
  const [openDiaolog, setOpenDialog] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);

  const {
    isVisible: isAbsHistoryDialogOpen,
    show: openAbsHistoryDialog,
    hide: closeAbsHistoryDialog,
  } = useVisible();
  return {
    defaultData,
    shortTermAbsences,
    vacHistoryTableData,
    bnEnableVacBank,
    bnEnableVacBalance,
    vacBankType1,
    vacBankType2,
    vacBankType3,
    openDiaolog,
    setOpenDialog,
    isAbsHistoryDialogOpen,
    closeAbsHistoryDialog,
    openAbsHistoryDialog,
    extraVacEnum,
    absShortReasonEnum,
    editMode,
    setEditMode,
    vacBankEnum,
  };
};
